import axios from "axios";
import { API_BASE } from "./api";

/**
 * Lightweight pinger that hits the public /api/ root with a short timeout.
 * Returns true if the server responded OK within `timeoutMs`.
 */
export async function pingApi(timeoutMs = 6000) {
  try {
    const res = await axios.get(`${API_BASE}/`, { timeout: timeoutMs });
    return res.status >= 200 && res.status < 300;
  } catch (e) {
    return false;
  }
}

/**
 * Start a recurring keep-alive ping. Returns a stop function.
 * Stops when the tab is hidden, resumes when visible.
 */
export function startKeepAlive({ intervalMs = 10 * 60 * 1000 } = {}) {
  let timer = null;
  let stopped = false;

  const tick = () => {
    if (document.hidden || stopped) return;
    pingApi(8000);
  };

  const start = () => {
    if (timer) return;
    tick();
    timer = setInterval(tick, intervalMs);
  };
  const stop = () => {
    if (timer) {
      clearInterval(timer);
      timer = null;
    }
  };
  const onVis = () => {
    if (document.hidden) stop();
    else start();
  };

  start();
  document.addEventListener("visibilitychange", onVis);

  return () => {
    stopped = true;
    document.removeEventListener("visibilitychange", onVis);
    stop();
  };
}

/**
 * Detect "the pod is sleeping / waking up" style failures.
 * - Network error / no response (often 502 from Cloudflare)
 * - Explicit 502 / 503 / 504
 * - Axios timeout
 */
export function isPodAsleepError(err) {
  if (!err) return false;
  if (err.code === "ECONNABORTED") return true; // timeout
  const status = err?.response?.status;
  if (status === 502 || status === 503 || status === 504) return true;
  // No response = network error / DNS / cloudflare cut off
  if (!err.response && (err.code === "ERR_NETWORK" || err.message?.includes("Network Error"))) {
    return true;
  }
  return false;
}

/**
 * Wake the pod up by hammering /api/ every `pollMs` until it responds OK
 * or `maxMs` elapses. `onTick(elapsedMs)` is called each attempt so the UI
 * can update a progress message.
 * Returns true if pod woke, false if max time elapsed.
 */
export async function waitForPod({
  maxMs = 30000,
  pollMs = 3000,
  onTick,
} = {}) {
  const startedAt = Date.now();
  while (Date.now() - startedAt < maxMs) {
    if (onTick) onTick(Date.now() - startedAt);
    const ok = await pingApi(5000);
    if (ok) return true;
    await new Promise((r) => setTimeout(r, pollMs));
  }
  return false;
}
